<template>
  <LayoutLegal>
    <h2 class="mt-2">Complaints & Content Removal</h2>

    <p>
      We take all content removal requests seriously and our dedicated support
      team works around the clock to quickly process and remove content that
      violates our Terms and services.
    </p>
    <p>
      Your report is completely confidential. When you report content, the user
      who posted the content will not see your name or any information about
      you.
    </p>
    <p>
      Please complete the form below should you be the victim of, or come across
      content that you have personal knowledge of as constituting:
    </p>

    <ul class="list-disc list-inside">
      <li>
        Non-consensual production and/or distribution of your image (including
        but not limited to such things as: revenge porn, blackmail,
        exploitation);
      </li>
      <li>
        Content that reveals personally identifiable information (including but
        not limited to such things as: name, address, phone number, IP address);
      </li>
      <li>Otherwise abusive and/or illegal content</li>
    </ul>

    <AppForm :state="state" class="space-y-4" @submit="onSubmit">
      <AppFormField
        label="First Name"
        name="firstName"
        :error="errorOn('first_name')"
      >
        <AppInput v-model="state.firstName" />
      </AppFormField>

      <AppFormField
        label="Last Name"
        name="lastName"
        :error="errorOn('last_name')"
      >
        <AppInput v-model="state.lastName" />
      </AppFormField>

      <AppFormField label="Email" name="email" :error="errorOn('email')">
        <AppInput v-model="state.email" />
      </AppFormField>

      <AppFormField
        label="URLs"
        name="urls"
        hint="Content you are reporting (separate multiple URLS with commas)"
        :error="errorOn('urls')"
      >
        <AppTextarea v-model="state.urls" :error="!!errorOn('urls')" />
      </AppFormField>

      <AppFormField
        label="Description"
        name="description"
        :error="errorOn('description')"
      >
        <AppTextarea
          v-model="state.description"
          :error="!!errorOn('description')"
        />
      </AppFormField>

      <p>
        Please provide any additional information which might help us to resolve
        your request. If you are submitting a request on the behalf of someone
        else appearing in the content, please provide your association to this
        person. Please be aware that abuse of this content removal request
        process hinders our team's ability to process valid and actionable
        removal requests.
      </p>

      <AppButton type="submit">Submit</AppButton>
    </AppForm>

    <h2 class="mt-8">How to Contact Us</h2>
    <div class="flex flex-col gap-3">
      <p class="leading-none">
        <strong>Email:</strong>
        <a href="mailto:cvv@cvvhelp.net">cvv@cvvhelp.net</a>
      </p>
    </div>
    <h2 class="mt-8">Complaint Policy</h2>
    <ol>
      <li class="mb-2">
        <strong>Introduction:</strong>
        This document sets out our complaints policy. If you are a User of
        <a :href="`https://${currentSite.host}`">{{ currentSite.host }}</a>
        this Complaints Policy forms part of your agreement with us.
      </li>
      <li class="mb-2">
        <strong>Interpretation</strong>
        : In this Complaints Policy, defined terms have the same meanings given
        to them in the
        <a href="/general-terms-of-service">Terms &amp; Conditions</a>
        . In addition, the term &quot;business days&quot; means any day which is
        not a Saturday, Sunday or public holiday in the United States of
        America.
      </li>
      <li class="mb-2">
        Who can use this Complaints Policy: Whether or not you are a User of
        <a :href="`https://${currentSite.host}`">{{ currentSite.host }},</a>
        you can use this Complaints Policy to alert us to any complaint which
        you have relating to
        <a :href="`https://${currentSite.host}`">{{ currentSite.host }}</a>
        .
      </li>
      <li class="mb-2">
        <strong>How to make a complaint:</strong>
        If you have a complaint about
        <a :href="`https://${currentSite.host}`">{{ currentSite.host }}</a>
        , (including any complaint about Content appearing on
        <a :href="`https://${currentSite.host}`">{{ currentSite.host }}</a>
        , or the conduct of a User), please submit your complaint by completing
        our online form on this page. You are required to complete all mandatory
        fields, provide a description of your complaint and, if your complaint
        relates to Content, the URL for the Content to which your complaint
        relates.
      </li>
      <li class="mb-2">
        <strong>
          How we deal with complaints of illegal or non-consensual content:
        </strong>
        Following receipt of your complaint of illegal or non-consensual Content
        under section 4 above:
        <ul class="list-disc ml-8 mt-3">
          <li class="mb-2">
            We will take such steps as we consider to be appropriate to resolve
            your complaint within 5 business days.
          </li>
          <li class="mb-2">
            If we require further information or documents from you, we will
            contact you to let you know.
          </li>
          <li class="mb-2">
            We will in good faith investigate your complaint and take the
            following action:
            <ul class="list-disc ml-8 mt-3">
              <li class="mb-2">
                If we are satisfied that the Content is unlawful or
                non-consensual, we will immediately remove such Content, and we
                will notify you of our decision by email or other electronic
                message.
              </li>
              <li class="mb-2">
                If we are satisfied that the Content is not unlawful or
                non-consensual, we will notify you of our decision by email or
                other electronic message.
              </li>
              <li class="mb-2">
                Any dispute regarding our decision on non-consensual content
                will be submitted by us to a neutral arbitration association at
                our expense.
              </li>
            </ul>
          </li>
        </ul>
      </li>
      <li class="mb-2">
        <strong>
          How we deal with complaints related to copyright infringement:
        </strong>
        Complaints related to copyright infringement must be submitted in
        accordance with our
        <a href="/dmca">DMCA Policy</a>
        , and we will respond to copyright infringement complaints as set out in
        such policy.
      </li>
      <li class="mb-2">
        <strong>How we deal with other complaints:</strong>
        Following receipt of other complaints (including complaints related to
        other breaches of our Acceptable Use Policy) under section 4 above:
        <ul class="list-disc ml-8 mt-3">
          <li class="mb-2">
            We will take such steps as we consider to be appropriate to
            investigate your complaint within a timescale which is appropriate
            to the nature of your complaint.
          </li>
          <li class="mb-2">
            If we require further information or documents from you, we will
            contact you to let you know.
          </li>
          <li class="mb-2">
            We will in good faith take such actions as we consider appropriate
            to deal with the issue which your complaint has raised. If you have
            complained about content which appears on
            <a :href="`https://${currentSite.host}`">{{ currentSite.host }}</a>
            and we are satisfied that the content otherwise breaches our
            Acceptable Use Policy, we will act quickly to remove such content.
          </li>
          <li class="mb-2">
            We are not obligated to inform you of the outcome of your complaint.
          </li>
        </ul>
      </li>
      <li class="mb-2">
        <strong>Unjustified or abusive complaints:</strong>
        If you are a User of
        <a :href="`https://${currentSite.host}`">{{ currentSite.host }},</a>
        you warrant (which means you make a legally enforceable promise) that
        you will not make any complaint under this Complaints Policy which is
        wholly unjustified, abusive, or made in bad faith. If we determine that
        you have breached this warranty, we may suspend or terminate your User
        account.
      </li>
    </ol>
  </LayoutLegal>
</template>

<script setup>
  useSiteSeo({
    defaultTitle: "Content Removal | {{ siteName }}",
    defaultDescription:
      "We take all content removal requests seriously and our dedicated support team works around the clock to quickly process and remove content that violates our Terms and services.",
  })

  const errors = ref({})

  const state = reactive({
    description: undefined,
    email: undefined,
    firstName: undefined,
    lastName: undefined,
    urls: undefined,
  })

  const currentSite = inject("currentSite")

  const toast = useAppToast()

  const fieldHasError = (field) => errors.value[field] !== undefined
  const errorOn = (field) =>
    fieldHasError(field) ? errors.value[field].join(" ") : undefined

  const onSubmit = async () => {
    const { data } = await useApi(`/api/content_removal`, {
      method: "POST",
      body: JSON.stringify({
        description: state.description,
        email: state.email,
        first_name: state.firstName,
        last_name: state.lastName,
        urls: state.urls,
      }),
    })

    if (data.value.success) {
      resetForm()
      toast.add({
        title: "Request submitted",
        description: "We will review your request and get back to you shortly.",
        color: "success",
      })
    } else {
      errors.value = data.value.errors
      toast.add({
        title: "Request failed",
        description: "Please check your form and try again.",
        color: "error",
      })
    }
  }

  const resetForm = () => {
    state.description = undefined
    state.email = undefined
    state.firstName = undefined
    state.lastName = undefined
    state.urls = undefined
  }
</script>
